(() => {
  const initializedSections = new WeakSet();
  const sectionStates = new WeakMap();
  const preloadedSources = new Set();
  const DESKTOP_QUERY = "(min-width: 750px)";
  const SHIFT_CLASS = "hover-image-v2-shift";

  function getHeaderHeight() {
    const value = parseFloat(getComputedStyle(document.body).getPropertyValue("--header-height"));
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * @param {string | undefined} value
   */
  function normalizeSource(value) {
    return typeof value === "string" ? value.trim() : "";
  }

  /**
   * @param {string} value
   */
  function isValidSource(value) {
    return value.length > 0 && !value.includes("no-image-2048");
  }

  /**
   * @param {HTMLElement} item
   */
  function getItemSource(item) {
    const isMobile = window.matchMedia("(max-width: 749px)").matches;
    const preferred = isMobile ? normalizeSource(item.dataset.mobileImage) : normalizeSource(item.dataset.image);
    const fallback = isMobile ? normalizeSource(item.dataset.image) : normalizeSource(item.dataset.mobileImage);
    return isValidSource(preferred) ? preferred : fallback;
  }

  /**
   * @param {string} source
   */
  function preloadSource(source) {
    if (!isValidSource(source) || preloadedSources.has(source)) {
      return;
    }

    preloadedSources.add(source);
    const image = new Image();
    image.decoding = "async";
    image.src = source;
  }

  /**
   * @param {HTMLElement} section
   * @returns {HTMLElement[]}
   */
  function getTriggers(section) {
    /** @type {HTMLElement[]} */
    const htmlItems = Array.from(section.querySelectorAll("[data-image]")).filter(
      (item) => item instanceof HTMLElement
    );

    return htmlItems.filter((item) => isValidSource(getItemSource(item)));
  }

  /**
   * @param {HTMLElement[]} triggers
   */
  function getInitialIndex(triggers) {
    const preferred = triggers.findIndex((item) => item.classList.contains("active__"));
    return preferred >= 0 ? preferred : 0;
  }

  /**
   * @param {HTMLElement} section
   */
  function getStageContainer(section) {
    const groupMediaContainer = section.querySelector(
      ".group-block:first-of-type:not([data-image]) .group-block__media-wrapper .background-image-container"
    );

    if (groupMediaContainer instanceof HTMLElement) {
      return groupMediaContainer;
    }

    const fallbackMediaContainer = section.querySelector(
      ".custom-section-background .background-image-container"
    );

    if (fallbackMediaContainer instanceof HTMLElement) {
      return fallbackMediaContainer;
    }

    return null;
  }

  /**
   * @param {HTMLImageElement | null} baseImage
   * @param {string} source
   * @param {boolean} active
   */
  function createLayer(baseImage, source, active) {
    const layer = baseImage ? baseImage.cloneNode(true) : document.createElement("img");

    if (!(layer instanceof HTMLImageElement)) {
      return null;
    }

    layer.classList.add("hover-image-v2-layer");
    layer.loading = "eager";
    layer.decoding = "async";
    layer.fetchPriority = active ? "high" : "auto";

    if (active) {
      layer.src = source;
      layer.srcset = source;
    } else {
      layer.dataset.source = source;
    }

    if (active) {
      layer.classList.add("is-active");
    }

    return layer;
  }

  /**
   * @param {HTMLImageElement} layer
   */
  function ensureLayerSource(layer) {
    const source = normalizeSource(layer.dataset.source);
    if (!isValidSource(source)) {
      return;
    }

    layer.src = source;
    layer.srcset = source;
    delete layer.dataset.source;
  }

  /**
   * Keeps the image within the screen on desktop. The row stretches the image
   * block to match the questions beside it, so a tall column of questions on a
   * short screen leaves the image running past the bottom of the screen. This
   * caps the image at the room the screen has and holds it there, letting the
   * questions scroll past instead.
   * @param {HTMLElement} section
   * @param {HTMLElement[]} triggers
   */
  function createFitImage(section, triggers) {
    const wrapper = section.querySelector(".section-content-wrapper");
    const image = wrapper ? wrapper.querySelector(":scope > .group-block:first-of-type:not([data-image])") : null;
    const firstTrigger = triggers[0];
    const column = firstTrigger ? firstTrigger.closest(".section-content-wrapper > .group-block") : null;

    if (!(image instanceof HTMLElement) || !(column instanceof HTMLElement) || image === column) {
      return null;
    }

    const questionColumn = column;
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);
    const spacing = Number(section.dataset.fitImageSpacing) || 0;
    const resizeObserver = "ResizeObserver" in window ? new ResizeObserver(() => refresh()) : null;

    /** Where the image settles on screen; sticky scroll supplies its own pinned top. */
    let getSettledTop = () => getHeaderHeight() + spacing;

    function isEnabled() {
      return desktopQuery.matches;
    }

    function refresh() {
      if (!section.isConnected) {
        destroy();
        return;
      }

      if (!isEnabled()) {
        section.style.removeProperty("--fit-image-top");
        section.style.removeProperty("--fit-image-height");
        return;
      }

      const settledTop = getSettledTop();
      const available = Math.max(0, window.innerHeight - settledTop - spacing);

      // Never taller than the questions beside it, so a section that already
      // fits keeps its proportions instead of being stretched to the screen.
      const height = Math.min(questionColumn.offsetHeight, available);

      section.style.setProperty("--fit-image-top", `${settledTop}px`);
      section.style.setProperty("--fit-image-height", `${height}px`);
    }

    /**
     * @param {() => number} provider
     */
    function setSettledTop(provider) {
      getSettledTop = provider;
    }

    function destroy() {
      window.removeEventListener("resize", refresh);
      desktopQuery.removeEventListener("change", refresh);
      resizeObserver?.disconnect();
    }

    window.addEventListener("resize", refresh);
    desktopQuery.addEventListener("change", refresh);
    resizeObserver?.observe(questionColumn);

    return { isEnabled, refresh, setSettledTop, column: questionColumn, image };
  }

  /**
   * Desktop sticky scroll. The section content pins below the header while the
   * page scrolls through a spacer track rendered after it, and each step of the
   * track activates the next trigger. Once the track ends the section scrolls
   * away as normal.
   * @param {HTMLElement} section
   * @param {HTMLElement[]} triggers
   * @param {(index: number) => void} setActive
   * @param {ReturnType<typeof createFitImage>} fitImage
   */
  function createStickyScroll(section, triggers, setActive, fitImage) {
    const content = section.querySelector(".custom-section-content");
    const track = section.querySelector(".hover-image-v2-sticky-track");

    if (!(content instanceof HTMLElement) || !(track instanceof HTMLElement)) {
      return null;
    }

    const stickyContent = content;
    const stickyTrack = track;
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);
    const distancePercent = Number(section.dataset.stickyScrollDistance) || 50;
    const topOffset = Number(section.dataset.stickyTopOffset) || 0;
    const lastIndex = triggers.length - 1;
    const resizeObserver = "ResizeObserver" in window ? new ResizeObserver(refresh) : null;

    let stepSize = 0;
    let stickyTop = 0;
    let activeIndex = -1;
    let ticking = false;
    /** @type {HTMLElement | null} */
    let shiftTarget = null;

    function isEnabled() {
      return desktopQuery.matches;
    }

    /**
     * With the image fitted to the screen only the questions slide, so the
     * image stays put; otherwise the whole pinned content slides.
     */
    function updateShiftTarget() {
      const target = fitImage && fitImage.isEnabled() ? fitImage.column : stickyContent;

      if (shiftTarget === target) {
        return;
      }

      shiftTarget?.classList.remove(SHIFT_CLASS);
      target.classList.add(SHIFT_CLASS);
      shiftTarget = target;
    }

    function measure() {
      if (!isEnabled()) {
        section.style.removeProperty("--sticky-top");
        section.style.removeProperty("--sticky-track-length");
        section.style.removeProperty("--sticky-shift");
        return;
      }

      stepSize = window.innerHeight * (distancePercent / 100);
      stickyTop = getHeaderHeight() + topOffset;

      section.style.setProperty("--sticky-top", `${stickyTop}px`);
      section.style.setProperty("--sticky-track-length", `${stepSize * lastIndex}px`);

      fitImage?.refresh();
      updateShiftTarget();
      applyShift(Math.max(activeIndex, 0));
    }

    /**
     * Vertical offset of an element inside the pinned content, taken from
     * layout rather than live rects so a transform still easing into place
     * cannot skew it.
     * @param {HTMLElement} el
     */
    function offsetInContent(el) {
      let offset = 0;
      /** @type {HTMLElement | null} */
      let node = el;

      while (node && node !== stickyContent) {
        offset += node.offsetTop;
        node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null;
      }

      return offset;
    }

    /**
     * More content than the screen allows would leave the lower blocks out of
     * sight while pinned, so whatever slides - the whole content, or only the
     * questions when the image is fitted to the screen - moves up as those
     * blocks become active, just far enough to bring each one into view. The
     * distance follows the room on screen rather than any fixed number of
     * blocks.
     * @param {number} index
     */
    function applyShift(index) {
      const trigger = triggers[index];
      const target = shiftTarget;

      if (!trigger || !target) {
        section.style.setProperty("--sticky-shift", "0px");
        return;
      }

      // Same gap at the bottom of the screen as the setting leaves below the header.
      const windowBottom = window.innerHeight - topOffset;
      const blockTop = offsetInContent(trigger);
      const blockBottom = blockTop + trigger.offsetHeight;

      // Never further than it takes to bring the sliding element's own bottom
      // up to the bottom of the screen.
      const maxShift = Math.max(0, stickyTop + offsetInContent(target) + target.offsetHeight - windowBottom);

      // Enough to bring the block's bottom into view, but never so far that its
      // own top is pushed above the pinned top.
      const needed = Math.min(Math.max(0, stickyTop + blockBottom - windowBottom), blockTop);
      const shift = Math.min(Math.max(needed, 0), maxShift);

      section.style.setProperty("--sticky-shift", `${shift}px`);
    }

    /**
     * How far the page has scrolled since the content pinned (negative before
     * it pins). The track sits directly under the content, so while pinned it
     * rises by exactly the scrolled distance.
     */
    function getScrolled() {
      return stickyTop + stickyContent.offsetHeight - stickyTrack.getBoundingClientRect().top;
    }

    function getIndex() {
      if (stepSize <= 0) {
        return 0;
      }

      return Math.min(lastIndex, Math.max(0, Math.round(getScrolled() / stepSize)));
    }

    function update() {
      ticking = false;

      if (!section.isConnected) {
        destroy();
        return;
      }

      if (!isEnabled()) {
        return;
      }

      const index = getIndex();
      if (index !== activeIndex) {
        activeIndex = index;
        setActive(index);
        applyShift(index);
      }
    }

    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    function refresh() {
      measure();
      activeIndex = -1;
      requestUpdate();
    }

    /**
     * @param {number} index
     */
    function scrollToIndex(index) {
      if (index === getIndex()) {
        return;
      }

      const pinStart = window.scrollY - getScrolled();
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      window.scrollTo({
        top: pinStart + index * stepSize,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    }

    function destroy() {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", refresh);
      desktopQuery.removeEventListener("change", refresh);
      resizeObserver?.disconnect();
    }

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", refresh);
    desktopQuery.addEventListener("change", refresh);
    resizeObserver?.observe(stickyContent);

    return { isEnabled, refresh, scrollToIndex, getStickyTop: () => stickyTop };
  }

  /**
   * @param {HTMLElement} section
   */
  function initSection(section) {
    if (initializedSections.has(section)) {
      return;
    }

    const triggers = getTriggers(section);
    if (!triggers.length) {
      return;
    }

    const stageContainer = getStageContainer(section);
    if (!(stageContainer instanceof HTMLElement)) {
      return;
    }

    const initialIndex = getInitialIndex(triggers);
    const initialTrigger = triggers[initialIndex] || triggers[0];
    if (!initialTrigger) {
      return;
    }

    const activeSource = getItemSource(initialTrigger);
    if (!isValidSource(activeSource)) {
      return;
    }

    const baseImage = stageContainer.querySelector("img");
    const stack = document.createElement("div");
    stack.className = "hover-image-v2-stack";
    stack.setAttribute("aria-hidden", "true");

    const layers = triggers
      .map((trigger, index) => {
        const source = getItemSource(trigger);
        return createLayer(baseImage instanceof HTMLImageElement ? baseImage : null, source, index === initialIndex);
      })
      .filter((layer) => layer instanceof HTMLImageElement);

    if (!layers.length) {
      return;
    }

    layers.forEach((layer) => stack.append(layer));
    stageContainer.append(stack);
    stageContainer.dataset.hoverImageV2Stage = "true";

    const initialLayer = layers[initialIndex] || layers[0];
    if (!initialLayer) {
      return;
    }

    const markReady = () => section.classList.add("hover-images-v2--ready");

    if (initialLayer.complete) {
      markReady();
      scheduleRemainingPreload();
    } else {
      initialLayer.addEventListener(
        "load",
        () => {
          markReady();
          scheduleRemainingPreload();
        },
        { once: true }
      );
      initialLayer.addEventListener(
        "error",
        () => {
          markReady();
          scheduleRemainingPreload();
        },
        { once: true }
      );
    }

    /**
     * @param {number} index
     */
    let activationRequestId = 0;

    /**
     * @param {number} index
     */
    function applyActiveState(index) {
      if (index < 0 || index >= triggers.length) {
        return;
      }

      triggers.forEach((trigger, triggerIndex) => {
        const active = triggerIndex === index;
        trigger.classList.toggle("active__", active);
        trigger.classList.toggle("is-active", active);
        trigger.setAttribute("aria-current", active ? "true" : "false");
      });

      layers.forEach((layer, layerIndex) => {
        layer.classList.toggle("is-active", layerIndex === index);
      });
    }

    /**
     * @param {number} index
     */
    function setActive(index) {
      if (index < 0 || index >= triggers.length) {
        return;
      }

      activationRequestId += 1;
      const requestId = activationRequestId;

      const targetLayer = layers[index];
      if (targetLayer) {
        if (targetLayer.classList.contains("is-active")) {
          applyActiveState(index);
          return;
        }

        const onReady = () => {
          if (requestId !== activationRequestId) {
            return;
          }

          applyActiveState(index);
        };

        ensureLayerSource(targetLayer);

        if (!targetLayer.complete) {
          targetLayer.addEventListener("load", onReady, { once: true });
          targetLayer.addEventListener("error", onReady, { once: true });
          return;
        }
      }

      applyActiveState(index);
    }

    function preloadRemainingLayers() {
      triggers.forEach((trigger, index) => {
        if (index === initialIndex) {
          return;
        }

        preloadSource(getItemSource(trigger));
      });
    }

    function scheduleRemainingPreload() {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => preloadRemainingLayers(), { timeout: 1500 });
      } else {
        setTimeout(() => preloadRemainingLayers(), 200);
      }
    }

    const fitImage = section.classList.contains("hover-images-v2--fit-image")
      ? createFitImage(section, triggers)
      : null;

    const sticky = section.classList.contains("hover-images-v2--sticky")
      ? createStickyScroll(section, triggers, setActive, fitImage)
      : null;

    // While pinned the image sits at the pinned top rather than its own.
    if (fitImage && sticky) {
      fitImage.setSettledTop(sticky.getStickyTop);
    }

    /**
     * In sticky scroll mode the scroll position decides the active block, so
     * selecting one scrolls to it rather than switching straight away.
     * @param {number} index
     */
    function selectTrigger(index) {
      if (sticky && sticky.isEnabled()) {
        sticky.scrollToIndex(index);
        return;
      }

      setActive(index);
    }

    triggers.forEach((trigger, index) => {
      if (!trigger.hasAttribute("tabindex") && !trigger.matches("a,button,input,select,textarea")) {
        trigger.tabIndex = 0;
      }

      /**
       * @param {KeyboardEvent} event
       */
      function onKeydown(event) {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();
        selectTrigger(index);
      }

      trigger.addEventListener("pointerenter", () => {
        // Sticky scroll replaces the hover effect.
        if (!sticky || !sticky.isEnabled()) {
          setActive(index);
        }
      });
      trigger.addEventListener("focusin", () => selectTrigger(index));
      trigger.addEventListener("click", () => selectTrigger(index));
      trigger.addEventListener("keydown", onKeydown);
    });

    setActive(initialIndex);
    fitImage?.refresh();
    sticky?.refresh();
    sectionStates.set(section, { triggers, selectTrigger });
    initializedSections.add(section);
  }

  /**
   * @param {ParentNode} root
   */
  function initSections(root) {
    root.querySelectorAll(".hover-images-v2").forEach((section) => {
      if (section instanceof HTMLElement) {
        initSection(section);
      }
    });
  }

  function boot() {
    initSections(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    initSections(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const section = event.target.closest(".hover-images-v2");
    if (!(section instanceof HTMLElement)) {
      return;
    }

    const state = sectionStates.get(section);
    if (!state) {
      return;
    }

    const trigger = event.target.closest("[data-image]");
    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    const index = state.triggers.indexOf(trigger);
    if (index >= 0) {
      state.selectTrigger(index);
    }
  });
})();
